import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formValidations } from '@/src/constants/FormValidations';

import {
  atomDetailCampaignRecurrent,
  atomFiltersRecurrent,
  atomHistorySearchPagerRecurrent,
  atomPagerCampaignsRecurrent,
} from '@/modules/CampaignsModule/atoms/campaignRecurrentAtom';
import { atomModalTypeProcess } from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { defaultPager } from '@/modules/CampaignsModule/constants';
import { useCampaignRecurrent } from '@/modules/CampaignsModule/hooks/useCampaignRecurrent';
import { useCampaignRecurrentFilters } from '@/modules/CampaignsModule/hooks/useCampaignRecurrentFilters';
import { useRecurrentModal } from '@/modules/CampaignsModule/hooks/useRecurrentModal';
import { useValidNameResource } from '@/modules/CampaignsModule/hooks/useValidNameResource';
import { EResourceType } from '@/modules/CampaignsModule/interfaces/CampaignActions';
import { ModalType } from '@/modules/CampaignsModule/interfaces/Campaigns';

export const useRecurrentForm = () => {
  const navigate = useNavigate();

  const filtersRecurrent = useAtomValue(atomFiltersRecurrent);
  const detailCampaign = useAtomValue(atomDetailCampaignRecurrent);
  const modalTypeProcess = useAtomValue(atomModalTypeProcess);
  const [historySearchPagerCampaign, setHistorySearchPagerCampaign] = useAtom(
    atomHistorySearchPagerRecurrent
  );
  const filters = useAtomValue(atomFiltersRecurrent);
  const pager = useAtomValue(atomPagerCampaignsRecurrent);
  const { t } = useTranslation();
  const { fetchServiceValidName, loadingValidation } = useValidNameResource();
  const { loading, createCampaignRecurrent, duplicateRecurrent } = useCampaignRecurrent();
  const { handleOnCloseModal, handleOnCloseDuplicateModal } = useRecurrentModal();
  const { getRecurrentCampaigns } = useCampaignRecurrentFilters();
  const { inputLengths, formRegExp } = formValidations;

  const VALIDATION_RULES = {
    input: {
      required: {
        value: true,
        message: t('FORMS_RULES_MESSAGES.required'),
      },
      minLength: {
        value: inputLengths.min,
        message: t('FORMS_RULES_MESSAGES.minLength'),
      },
      maxLength: {
        value: inputLengths.max,
        message: t('FORMS_RULES_MESSAGES.maxLength'),
      },
      pattern: {
        value: formRegExp.lettersNumbersAndSpecials,
        message: t('FORMS_RULES_MESSAGES.lettersNumbersAndSpecials'),
      },
    },
  };

  const handleCloseModal = useCallback(() => handleOnCloseModal(), [handleOnCloseModal]);

  const handleValidation = useCallback(
    async (value: string) => {
      const isValidName = await fetchServiceValidName({
        name: value,
        resource: EResourceType.CAMPAIGN,
      });
      return {
        isValidInput: isValidName,
        message: t('RECURRENT_MESSAGE.CREATE_RECURRENT.existCampaignRecurrent'),
      };
    },
    [fetchServiceValidName, t]
  );

  const handleSubmitData = useCallback(
    async (data: { name: string }) => {
      const { campaignId, actionId } = await createCampaignRecurrent(data.name.trim());
      handleCloseModal();

      modalTypeProcess === ModalType.CREATE
        ? navigate(`/v2/campaigns/${campaignId}/master/${actionId}`)
        : await getRecurrentCampaigns({ ...filtersRecurrent });
    },
    [
      createCampaignRecurrent,
      filtersRecurrent,
      getRecurrentCampaigns,
      handleCloseModal,
      modalTypeProcess,
      navigate,
    ]
  );

  const handleDuplicate = useCallback(
    async (value: string) => {
      const campaignIdDuplicate = await duplicateRecurrent({
        name: value,
        campaignId: Number(detailCampaign.campaignId),
      });

      handleOnCloseDuplicateModal();

      if (campaignIdDuplicate) {
        if (!historySearchPagerCampaign) {
          setHistorySearchPagerCampaign({
            filters: { ...filters },
            pager: {
              page: pager?.page || defaultPager.page,
              limit: pager?.limit || defaultPager.limit,
            },
          });
        }
        navigate(`/v2/campaigns/recurrent/${campaignIdDuplicate}`);
      }
    },
    [
      duplicateRecurrent,
      detailCampaign.campaignId,
      handleOnCloseDuplicateModal,
      historySearchPagerCampaign,
      navigate,
      setHistorySearchPagerCampaign,
      filters,
      pager?.page,
      pager?.limit,
    ]
  );

  return {
    handleValidation,
    handleDuplicate,
    handleCloseModal,
    handleSubmitData,
    VALIDATION_RULES,
    loading: loading || loadingValidation,
  };
};
